
import React, { useRef, useState } from "react";
import MediaBubble from "./MediaBubble";

export default function MessageBubble({ msg, isMine, onReply, recipientName, sharedKeyRef }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const startX = useRef(0);
  const currentX = useRef(0);
  const bubbleRef = useRef(null);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    currentX.current = e.touches[0].clientX;
    const diff = currentX.current - startX.current;
    // Limit drag distance and only allow drag in one direction based on message owner
    const maxDrag = 50;
    const dragDistance = isMine ? 
      Math.max(Math.min(diff, 0), -maxDrag) : 
      Math.min(Math.max(diff, 0), maxDrag);
    setDragOffset(dragDistance);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // If dragged far enough, trigger reply
    if (Math.abs(dragOffset) > 30) {
      onReply(msg);
    }
    setDragOffset(0);
  };

  return (
    <div className={`d-flex mb-2 ${isMine ? "justify-content-end" : "justify-content-start"}`}
         style={{ position: 'relative' }}>
      {/* Reply indicator icon */}
      {dragOffset !== 0 && (
        <div 
          className={`position-absolute top-50 translate-middle-y d-flex align-items-center ${isMine ? 'end-100 me-2' : 'start-100 ms-2'}`}
          style={{ 
            opacity: Math.min(Math.abs(dragOffset) / 30, 1),
            color: '#0d6efd',
            transition: 'opacity 0.2s'
          }}
        >
          <i className={`bi bi-reply${isMine ? '-fill' : ''} fs-5`}></i>
        </div>
      )}

      <div
        ref={bubbleRef}
        className={`d-flex flex-column rounded px-3 py-2 position-relative ${
          isMine ? "bg-success text-white align-items-end" : "bg-white text-dark align-items-start"
        }`}
        style={{ 
          maxWidth: "75%",
          transform: `translateX(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Reply content if this message is a reply */}
        {msg.replyTo && (
          <div 
            className={`mb-2 ${isMine ? 'text-white' : 'text-dark'}`}
            style={{ fontSize: '0.85rem', opacity: 0.85, paddingLeft: '0.5rem', marginLeft: '-0.25rem', borderLeft: '3px solid', borderColor: isMine ? 'rgba(255,255,255,0.5)' : '#0d6efd' }}
          >
            <div className={isMine ? 'text-white' : 'text-primary'} style={{ fontSize: '0.8rem', fontWeight: 500 }}>
              {msg.replyTo.sender === "You" ? 'You' : recipientName}
            </div>
            <div className="text-truncate" style={{ maxWidth: '200px' }}>
              {msg.replyTo.message}
            </div>
          </div>
        )}

        {/* Media content if message has media */}
        {msg.mediaUrl && msg.mediaType ? (
          <MediaBubble msg={msg} sharedKeyRef={sharedKeyRef} isMine={isMine} />
        ) : (
          <div className="mb-1">{msg.message}</div>
        )}

        {/* Reply button */}
        <button
          className={`btn btn-sm position-absolute top-50 start-0 translate-middle-y ${isMine ? 'btn-light' : 'btn-secondary'}`}
          onClick={() => onReply && onReply(msg)}
          style={{ 
            opacity: 0,
            transition: 'opacity 0.2s',
            marginLeft: '-1.5rem',
            padding: '0.2rem 0.4rem'
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0}
        >
          <i className="bi bi-reply-fill"></i>
        </button>
        {msg.timestamp && !isNaN(Date.parse(msg.timestamp)) && (
          <div
            className={`d-flex align-items-center gap-2 ${isMine ? "text-white-50" : "text-secondary"}`}
            style={{ fontSize: "0.75rem" }}
            title={new Date(msg.timestamp).toLocaleString()}
          >
            <span>
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
            </span>
            {isMine && (
              <span className="ms-1">
                {msg.status === 'seen' ? (
                  <i className="bi bi-check2-all" style={{ color: '#0d6efd' }} title="Seen" />
                ) : msg.status === 'delivered' ? (
                  <i className="bi bi-check2-all" title="Delivered" />
                ) : (
                  <i className="bi bi-check2" title="Sent" />
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
